import React, { Component } from 'react';
import WaveSurfer from 'wavesurfer.js';
import SmallLoader from '../utility/SmallLoader/SmallLoader';
import './wavePlayer.scss';
const seekDuration = 5;

class Waveform extends Component {
  waveform;
  waverendered = false;
  _mountAttempts = 0;
  constructor(props) {
    super(props);
    this.state = { playing: false, canplay: false };
    this.containerRef = React.createRef();

    this.togglePlaying = this.togglePlaying.bind(this);
    this.seekLeft = this.seekLeft.bind(this);
    this.seekRight = this.seekRight.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  getAccentColor() {
    const root = document.documentElement;
    const accentColor = getComputedStyle(root).getPropertyValue('--color-accent').trim();
    return accentColor;
  }

  attachKeyListeners() {
    document.addEventListener('keydown', this.handleKeyPress);
  }

  detachKeyListeners() {
    document.removeEventListener('keydown', this.handleKeyPress);
  }

  togglePlaying() {
    const shouldPlay = !this.state.playing;
    // Extract the original track ID from previewAudioPlayer-{id} format
    const trackId = this.props.id.split('-')[1];
    this.props.setActiveTrack && this.props.setActiveTrack(trackId);
    this.setState({ playing: shouldPlay });
    if (shouldPlay) {
      this.waveform.play();
    } else {
      this.waveform.pause();
    }
  }

  componentDidMount() {
    this._mountAttempts = 0;
    this.tryInitWaveSurfer();
    if (this.props?.isActiveTrack) {
      this.attachKeyListeners();
    }
  }

  tryInitWaveSurfer() {
    if (this.waverendered) return;
    const accentColor = this.getAccentColor();
    const containerElem = this.containerRef.current;
    // console.log('WavePlayer mounting (ref):', this.props.id, containerElem);
    if (!containerElem) {
      if (this._mountAttempts < 5) {
        this._mountAttempts++;
        setTimeout(() => this.tryInitWaveSurfer(), 50);
      } else {
        // console.warn('WavePlayer: Container element not found for ref', this.props.id);
      }
      return;
    }
      this.waverendered = true;
      this.waveform = WaveSurfer.create({
        barWidth: 1,
        cursorWidth: 1,
        container: containerElem,
        height: 30,
        progressColor: accentColor,
        waveColor: 'rgba(255, 255, 255, 0.25)',
        cursorColor: 'transparent',
        // backend: 'MediaElement',
      });

      if (!!this.props.file) {
        this.waveform.load(this.props.file);
      } else {
        this.waveform.loadBlob(this.props.blob);
      }

      this.waveform.on('ready', () => {
      this.setState({ canplay: true }, () => {
        // Call onDuration prop with duration in seconds
        if (typeof this.props.onDuration === 'function') {
          const duration = this.waveform.getDuration();
          this.props.onDuration(duration);
        }
        // console.log('Waveform is ready with new file');
        if (this.props.isActiveTrack) {
          this.autoplayIfNeeded();
    }
      });
    });
    // Listen for playback end to reset play state
    this.waveform.on('finish', () => {
      this.setState({ playing: false });
    });
  }

  componentDidUpdate(prevProps) {
    // Check if the active track status has changed
    if (!prevProps?.isActiveTrack && this.props?.isActiveTrack) {
      this.attachKeyListeners();
      this.autoplayIfNeeded();
    } else if (prevProps?.isActiveTrack && !this.props?.isActiveTrack) {
      this.waveform.pause();
      this.setState({ playing: false });
      this.detachKeyListeners();
    }

    // Check if the file prop has changed
    if (prevProps.file !== this.props.file) {
      const audioSource = this.props.file ? this.props.file : this.props.blob;
      this.waveform.load(audioSource);

      // Optionally reset playback state
      this.setState({ playing: false, canplay: false });

      // Listen for the waveform to be ready again
      this.waveform.on('ready', () => {
        this.setState({ canplay: true }, () => {
          // console.log('Waveform is ready with new file');
          if (this.props.isActiveTrack) {
          this.autoplayIfNeeded();
          }
        });
      });
    }
  }

  seekLeft() {
    // If at end, seek to 0 and play
    if (this.waveform.getCurrentTime() >= this.waveform.getDuration() - 0.01) {
      this.waveform.seekTo(0);
      this.waveform.play();
      this.setState({ playing: true });
    } else {
      this.waveform.seekTo(0);
      if (!this.state.playing) {
        this.togglePlaying();
      }
    }
  }

  seekRight() {
    const currentTime = this.waveform.getCurrentTime();
    const newTime =
      currentTime + seekDuration > this.waveform.getDuration()
        ? this.waveform.getDuration()
        : currentTime + seekDuration;
    this.waveform.seekTo(newTime / this.waveform.getDuration());
    if (!this.state.playing) {
      this.togglePlaying();
    }
  }
  handleKeyPress(event) {
    const { keyCode } = event;
    // Skip spacebar handling completely if in input/textarea
    if (keyCode === 32 && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
      return;
    }
    // If a modal is open, only players inside the modal should respond
    const openModal = document.querySelector('.modal[role="dialog"]') || document.querySelector('.modal.show');
    if (openModal) {
      const playerEl = this.containerRef?.current;
      if (!playerEl) return;
      if (!playerEl.closest('.modal')) return;
    }
    switch (keyCode) {
      case 32: // Spacebar
        event.preventDefault();
        this.togglePlaying();
        break;
      case 37: // Left arrow
        this.seekLeft();
        break;
      case 39: // Right arrow
        this.seekRight();
        break;
      default:
        break;
    }
  }

  componentWillUnmount() {
    this.detachKeyListeners();
    if (this.waverendered) {
      this.waverendered = false;
      // Stop any playing audio
      if (this.waveform.isPlaying()) {
        this.waveform.stop();
      }
      // Remove all event listeners
      this.waveform.unAll();
      // Clear the audio buffer
      if (this.waveform.backend) {
        this.waveform.backend.destroy();
      }
      // Destroy the waveform instance
      this.waveform.destroy();
      // Clear any references
      this.waveform = null;
    }
  }

  autoplayIfNeeded() {
    if (this.state.canplay && this.props.isActiveTrack && !this.props.noAutoplay) {
      this.waveform.play();
      this.setState({ playing: true });
    }
  }

  render() {
    const { playing, canplay } = this.state;
    return (
      <div
        className={
          'wave-player d-flex flex-row align-items-center justify-content-center w-100 py-2'
        }
      >
        <SmallLoader
          className={`small-loader position-absolute ${
            !canplay ? '' : 'hideUntilLoaded'
          }`}
        />
        <div
          className={`wave-player-button-wrap d-flex align-items-center justify-content-center ${
            canplay ? '' : 'hideUntilLoaded'
          }`}
        >
          {canplay && (
            <i
              onClick={this.togglePlaying}
              className={`tim-icons ${
                !playing
                  ? 'icon-triangle-right-17'
                  : 'icon-button-pause playing'
              } text-accent`}
            ></i>
          )}
        </div>
        <div
          ref={this.containerRef}
          className={`wave-form w-100 flex-grow-1 ${
            canplay ? '' : 'hideUntilLoaded'
          }`}
        ></div>
      </div>
    );
  }
}

export default React.memo(Waveform);
